import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import type {
  CertNotifyContact,
  CertTypeRow,
  EmployeeCertInput,
  EmployeeCertRow,
  EmployeeCerts,
} from '@/api/types'
import {
  EXPIRY_RULE_LABEL,
  OUTCOME_LABEL,
  STATE_LABEL,
  badgeClass,
  dayLabel,
  expiryPhrase,
  outcomeTone,
  stateTone,
  useCertAccess,
} from '@/lib/certs'

/**
 * One person's certifications: what they hold, how it meets each
 * requirement, and the full history.
 *
 * APPEND-ONLY, AND THE SCREEN SAYS SO. Renew adds a new card that names the
 * one it replaces; Correct fixes a typo and is audited field by field; Void
 * takes a record out of the reckoning without deleting it. There is no
 * Delete. A date change asks why, because dates decide whether someone counts
 * as certified.
 *
 * Its own page rather than a tab on Employees, so the Employees screen Tim is
 * testing is untouched until the module opens to everyone (design §7).
 */

type Mode =
  | { kind: 'add' }
  | { kind: 'renew'; cert: EmployeeCertRow }
  | { kind: 'correct'; cert: EmployeeCertRow }
  | { kind: 'void'; cert: EmployeeCertRow }
  | { kind: 'reevaluate'; cert: EmployeeCertRow }

/** issued + months, clamped to month end — the same rule the server uses. */
function addMonths(iso: string, months: number): string {
  const [y, m, d] = iso.split('-').map(Number)
  const total = m - 1 + months
  const ny = y + Math.floor(total / 12)
  const nm = (total % 12) + 1
  const last = new Date(Date.UTC(ny, nm, 0)).getUTCDate()
  return `${ny}-${String(nm).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`
}

function errText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message
  return err instanceof Error ? err.message : fallback
}

export function EmployeeCertsPage() {
  const { employeeId } = useParams()
  const id = Number(employeeId)
  const access = useCertAccess()

  const [data, setData] = useState<EmployeeCerts | null>(null)
  const [types, setTypes] = useState<CertTypeRow[]>([])
  const [asOf, setAsOf] = useState('')
  const [mode, setMode] = useState<Mode | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      setData(await api.employeeCerts(id, asOf || undefined))
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 404 && !access.enabled
          ? 'Certifications are not switched on for your account.'
          : errText(err, 'Could not load'),
      )
    }
  }, [id, asOf, access.enabled])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    void api.certTypes().then(setTypes).catch(() => setTypes([]))
  }, [])

  const typeById = useMemo(() => new Map(types.map((t) => [t.cert_type_id, t])), [types])

  /** May this user change records of this type? Medical types need Admin. */
  const canWrite = (restricted: boolean, verb: 'record' | 'verify' | 'void' = 'record') =>
    access.can(verb) && (!restricted || access.can('write_restricted'))

  /** Run a write; every write returns the whole refreshed page. */
  async function run(label: string, fn: () => Promise<EmployeeCerts>) {
    setError(null)
    setNotice(null)
    try {
      setData(await fn())
      setMode(null)
      setNotice(label)
    } catch (err) {
      setError(errText(err, 'Could not save. Nothing was changed.'))
    }
  }

  if (!data) {
    return (
      <div className="stack">
        {error ? <div className="alert alert-error">{error}</div> : <p className="muted">Loading…</p>}
        <Link to="/certs">← Certifications</Link>
      </div>
    )
  }

  const p = data.person
  const viewingPast = asOf !== ''

  return (
    <div className="stack">
      <Link to="/certs">← Certifications</Link>
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-ok">{notice}</div>}

      <div className="card">
        <div className="crew-heading">
          <h2 style={{ margin: 0 }}>
            {p.display_name} <span className="mono muted" style={{ fontSize: '0.9rem' }}>{p.employee_no}</span>
          </h2>
          {access.can('use_as_of') && (
            <div className="form-field" style={{ margin: 0 }}>
              <label htmlFor="personAsOf">As of</label>
              <input id="personAsOf" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
            </div>
          )}
        </div>
        <p className="muted" style={{ marginBottom: 0 }}>
          {p.portal_role}
          {!p.is_active && ' · inactive'}
          {p.is_provisional && ' · new hire not yet in Sage'}
          {' · '}status as of {dayLabel(data.as_of)}
          {viewingPast && ' (looking back — cards issued after this date are not counted)'}
        </p>
        {p.linked_employee_id && (
          <div className="alert alert-info" style={{ marginTop: 12, marginBottom: 0 }}>
            This new-hire record was linked to their Sage record. Their certifications count there.{' '}
            <Link to={`/certs/people/${p.linked_employee_id}`}>Open the linked person</Link>
          </div>
        )}
      </div>

      {data.contact && !p.linked_employee_id && (
        <AlertEmailCard
          employeeId={p.employee_id}
          contact={data.contact}
          onSaved={(c) => {
            setData({ ...data, contact: c })
            setNotice('Alert email saved.')
          }}
          onError={setError}
        />
      )}

      {/* Requirements first: "is this person OK to work" is the question. */}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Requirements</h3>
        {data.requirements.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            No requirement applies to this person
            {access.can('manage_requirements') && (
              <> — <Link to="/certs/requirements">set requirements</Link></>
            )}
            .
          </p>
        ) : (
          <div className="table-wrap table-dense">
            <table>
              <thead>
                <tr>
                  <th>Certification</th>
                  <th>Why it applies</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.requirements.map((q) => (
                  <tr key={q.cert_requirement_id}>
                    <td>{q.type_name}</td>
                    <td className="muted">
                      {q.scope_label}
                      {q.scope_job_no && q.scope_type !== 'Job' && ` (worked ${q.scope_job_no})`}
                    </td>
                    <td>
                      <span className={badgeClass(outcomeTone(q.outcome))}>{OUTCOME_LABEL[q.outcome]}</span>
                      {q.outcome === 'Missing' && q.best_cert_state === 'Pending' && (
                        <span className="muted"> — one is awaiting verification</span>
                      )}
                      {q.best_expires_date && q.outcome !== 'Missing' && (
                        <span className="muted"> — {dayLabel(q.best_expires_date)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="crew-heading">
          <h3 style={{ margin: 0 }}>Current certifications</h3>
          {access.can('record') && !p.linked_employee_id && !viewingPast && (
            <button className="btn btn-sm" onClick={() => setMode({ kind: 'add' })}>
              Add a certification
            </button>
          )}
        </div>

        {mode?.kind === 'add' && (
          <CertForm
            title="Add a certification"
            types={types.filter((t) => t.is_active && (!t.is_restricted || access.can('write_restricted')))}
            onCancel={() => setMode(null)}
            onSubmit={(payload) => run('Certification added.', () => api.addCert(p.employee_id, payload))}
          />
        )}

        {data.current.length === 0 ? (
          <p className="muted" style={{ marginBottom: 0 }}>Nothing on file.</p>
        ) : (
          <div className="table-wrap table-dense">
            <table>
              <thead>
                <tr>
                  <th>Certification</th>
                  <th>Status</th>
                  <th>Issued</th>
                  <th>Expiry</th>
                  <th>Number / provider</th>
                  <th>Recorded</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.current.map((c) => (
                  <CertRowView
                    key={c.employee_cert_id}
                    c={c}
                    type={typeById.get(c.cert_type_id)}
                    mode={mode}
                    setMode={setMode}
                    canWrite={canWrite}
                    canReevaluate={access.can('reevaluate')}
                    readOnly={viewingPast}
                    run={run}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>History</h3>
        {data.history.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>No earlier or voided records.</p>
        ) : (
          <div className="table-wrap table-dense">
            <table>
              <thead>
                <tr>
                  <th>Certification</th>
                  <th>Record</th>
                  <th>Issued</th>
                  <th>Expiry</th>
                  <th>Recorded</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {data.history.map((h) => (
                  <tr key={h.employee_cert_id} style={h.record_status === 'Voided' ? { opacity: 0.7 } : undefined}>
                    <td>{h.type_name}</td>
                    <td>
                      {h.record_status === 'Voided' ? (
                        <span className="badge">Voided</span>
                      ) : (
                        <span className="muted">Superseded</span>
                      )}
                    </td>
                    <td>{h.redacted ? '—' : dayLabel(h.issued_date)}</td>
                    <td>{h.redacted ? '—' : h.expires_date ? dayLabel(h.expires_date) : 'No expiry'}</td>
                    <td className="muted">
                      {h.recorded_by ?? '—'} · {h.source}
                    </td>
                    <td className="muted">
                      {h.redacted
                        ? 'Restricted'
                        : h.record_status === 'Voided'
                          ? `Voided by ${h.voided_by ?? '—'}: ${h.void_reason ?? ''}`
                          : h.note ?? ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// --------------------------------------------------------------------------
// One current certification, with its actions and inline editors.
// --------------------------------------------------------------------------

function CertRowView({
  c,
  type,
  mode,
  setMode,
  canWrite,
  canReevaluate,
  readOnly,
  run,
}: {
  c: EmployeeCertRow
  type: CertTypeRow | undefined
  mode: Mode | null
  setMode: (m: Mode | null) => void
  canWrite: (restricted: boolean, verb?: 'record' | 'verify' | 'void') => boolean
  canReevaluate: boolean
  readOnly: boolean
  run: (label: string, fn: () => Promise<EmployeeCerts>) => Promise<void>
}) {
  const open = mode && 'cert' in mode && mode.cert.employee_cert_id === c.employee_cert_id ? mode : null
  const [reason, setReason] = useState('')

  return (
    <>
      <tr>
        <td>
          {c.type_name}
          {c.is_restricted && <div className="muted" style={{ fontSize: '0.75rem' }}>Restricted (medical)</div>}
        </td>
        <td>
          {c.cert_state && <span className={badgeClass(stateTone(c.cert_state))}>{STATE_LABEL[c.cert_state]}</span>}
          {c.cert_state === 'BlockedByPrereq' || (c.cert_state === 'Expired' && (c.prereq_lapsed || c.prereq_out_of_order)) ? (
            <div className="muted" style={{ fontSize: '0.75rem', maxWidth: 260 }}>
              {c.redacted
                ? 'A prerequisite is not in order.'
                : [
                    c.prereq_lapsed && 'A prerequisite has lapsed.',
                    c.prereq_out_of_order && 'A prerequisite was not in place when this was issued.',
                  ].filter(Boolean).join(' ')}
            </div>
          ) : null}
        </td>
        <td>{c.redacted ? '—' : dayLabel(c.issued_date)}</td>
        <td>
          {expiryPhrase(c.cert_state, c.expires_date, c.redacted)}
          {!c.redacted && c.days_to_expiry !== null && c.days_to_expiry >= 0 && c.cert_state === 'Expiring' && (
            <span className="muted"> ({c.days_to_expiry} days)</span>
          )}
        </td>
        <td>
          {c.redacted ? (
            <span className="muted">Restricted</span>
          ) : (
            <>
              {c.cert_number && <span className="mono">{c.cert_number}</span>}
              {c.cert_number && c.provider && ' · '}
              {c.provider}
              {c.attributes &&
                Object.entries(c.attributes).map(([k, v]) => (
                  <div key={k} className="muted" style={{ fontSize: '0.75rem' }}>
                    {type?.attribute_spec.find((s) => s.key === k)?.label ?? k}: {v}
                  </div>
                ))}
              {c.note && <div className="muted" style={{ fontSize: '0.75rem' }}>{c.note}</div>}
            </>
          )}
        </td>
        <td className="muted" style={{ fontSize: '0.8125rem' }}>
          {c.recorded_by ?? '—'}
          <div>{c.record_status === 'Pending' ? 'Not yet verified' : `Verified${c.verified_by ? ` by ${c.verified_by}` : ''}`}</div>
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          {!readOnly && (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {canWrite(c.is_restricted) && (
                <>
                  <button className="btn btn-secondary btn-sm" onClick={() => setMode({ kind: 'renew', cert: c })}>
                    Renew
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setMode({ kind: 'correct', cert: c })}>
                    Correct
                  </button>
                </>
              )}
              {c.record_status === 'Pending' && canWrite(c.is_restricted, 'verify') && (
                <button
                  className="btn btn-sm"
                  onClick={() => void run('Verified.', () => api.verifyCert(c.employee_cert_id))}
                >
                  Verify
                </button>
              )}
              {canWrite(c.is_restricted, 'void') && (
                <button className="btn btn-secondary btn-sm" onClick={() => setMode({ kind: 'void', cert: c })}>
                  Void
                </button>
              )}
              {canReevaluate && (
                <button className="btn btn-secondary btn-sm" onClick={() => setMode({ kind: 'reevaluate', cert: c })}>
                  {c.reevaluation_required ? 'Clear re-evaluation' : 'Needs re-evaluation'}
                </button>
              )}
            </div>
          )}
        </td>
      </tr>

      {open && (
        <tr>
          <td colSpan={7}>
            {open.kind === 'renew' && type && (
              <CertForm
                title={`Renew ${c.type_name}`}
                types={[type]}
                fixedType={type}
                onCancel={() => setMode(null)}
                onSubmit={(payload) =>
                  run('Renewal recorded. The earlier card is kept in the history.', () =>
                    api.renewCert(c.employee_cert_id, payload),
                  )
                }
              />
            )}
            {open.kind === 'correct' && type && (
              <CertForm
                title={`Correct ${c.type_name}`}
                types={[type]}
                fixedType={type}
                initial={c}
                onCancel={() => setMode(null)}
                onSubmit={(payload) =>
                  run('Corrected. The change is in the audit trail.', () =>
                    api.correctCert(c.employee_cert_id, payload),
                  )
                }
              />
            )}
            {(open.kind === 'void' || open.kind === 'reevaluate') && (
              <div className="stack" style={{ maxWidth: 520 }}>
                <div className="form-field">
                  <label htmlFor={`reason-${c.employee_cert_id}`}>
                    {open.kind === 'void'
                      ? 'Why is this record being voided?'
                      : c.reevaluation_required
                        ? 'Why is re-evaluation no longer needed?'
                        : 'What has triggered a re-evaluation?'}
                  </label>
                  <input
                    id={`reason-${c.employee_cert_id}`}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={open.kind === 'void' ? 'e.g. entered on the wrong person' : ''}
                  />
                </div>
                <p className="muted" style={{ margin: 0, fontSize: '0.8125rem' }}>
                  {open.kind === 'void'
                    ? 'The record stays in the history. If there is an earlier card, it becomes current again.'
                    : 'Until cleared, this certification does not count.'}
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="btn btn-sm"
                    disabled={!reason.trim()}
                    onClick={() =>
                      void run(
                        open.kind === 'void' ? 'Voided.' : 'Re-evaluation updated.',
                        () =>
                          open.kind === 'void'
                            ? api.voidCert(c.employee_cert_id, reason.trim())
                            : api.reevaluateCert(c.employee_cert_id, !c.reevaluation_required, reason.trim()),
                      ).then(() => setReason(''))
                    }
                  >
                    {open.kind === 'void' ? 'Void this record' : 'Save'}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setMode(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

// --------------------------------------------------------------------------
// Add / renew / correct. The type's expiry rule decides what is asked:
//   FixedMonths  expiry computed and shown; override only with a reason
//   CardDate     the date on the card, required
//   None         no expiry field at all
// --------------------------------------------------------------------------

function CertForm({
  title,
  types,
  fixedType,
  initial,
  onSubmit,
  onCancel,
}: {
  title: string
  types: CertTypeRow[]
  fixedType?: CertTypeRow
  initial?: EmployeeCertRow
  onSubmit: (payload: EmployeeCertInput) => Promise<void>
  onCancel: () => void
}) {
  const correcting = initial !== undefined
  const [typeId, setTypeId] = useState<number | ''>(fixedType?.cert_type_id ?? '')
  const [issued, setIssued] = useState(initial?.issued_date ?? '')
  const [expires, setExpires] = useState(initial?.expires_date ?? '')
  // A card saved with a date other than the computed one opens showing that
  // date, not the computed one -- otherwise correcting its number would show
  // an expiry that is not what is stored.
  const [override, setOverride] = useState(
    Boolean(
      initial?.issued_date && initial.expires_date &&
        fixedType?.expiry_rule === 'FixedMonths' && fixedType.validity_months &&
        initial.expires_date !== addMonths(initial.issued_date, fixedType.validity_months),
    ),
  )
  const [number, setNumber] = useState(initial?.cert_number ?? '')
  const [provider, setProvider] = useState(initial?.provider ?? '')
  const [note, setNote] = useState(initial?.note ?? '')
  const [attrs, setAttrs] = useState<Record<string, string>>(initial?.attributes ?? {})
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const type = types.find((t) => t.cert_type_id === typeId)
  const computed =
    type?.expiry_rule === 'FixedMonths' && issued && type.validity_months
      ? addMonths(issued, type.validity_months)
      : null

  const datesChanged =
    correcting && (issued !== (initial?.issued_date ?? '') || (override || type?.expiry_rule === 'CardDate'
      ? expires !== (initial?.expires_date ?? '')
      : false))
  const needsReason = (override && computed !== null && expires !== computed) || datesChanged

  async function submit() {
    if (!type) return
    const payload: EmployeeCertInput = {}
    const blank = (s: string) => (s.trim() === '' ? null : s.trim())
    const sendExpiry = type.expiry_rule === 'CardDate' || (type.expiry_rule === 'FixedMonths' && override)

    if (correcting && initial) {
      // Only what changed. Absent keys are left alone by the server.
      if (issued !== initial.issued_date) payload.issued_date = issued
      if (sendExpiry && expires !== (initial.expires_date ?? '')) payload.expires_date = expires || null
      if (blank(number) !== initial.cert_number) payload.cert_number = blank(number)
      if (blank(provider) !== initial.provider) payload.provider = blank(provider)
      if (blank(note) !== initial.note) payload.note = blank(note)
      if (JSON.stringify(attrs) !== JSON.stringify(initial.attributes ?? {})) payload.attributes = attrs
    } else {
      payload.cert_type_id = type.cert_type_id
      payload.issued_date = issued
      if (sendExpiry) payload.expires_date = expires || null
      payload.cert_number = blank(number)
      payload.provider = blank(provider)
      payload.note = blank(note)
      payload.attributes = Object.keys(attrs).length ? attrs : null
    }
    if (reason.trim()) payload.reason = reason.trim()

    setBusy(true)
    try {
      await onSubmit(payload)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card" style={{ background: 'var(--c-surface-alt)', margin: '0.75rem 0' }}>
      <h4 style={{ marginTop: 0 }}>{title}</h4>
      <div className="field-grid">
        {!fixedType && (
          <div className="form-field">
            <label htmlFor="cfType">Certification</label>
            <select
              id="cfType"
              value={typeId}
              onChange={(e) => {
                setTypeId(e.target.value ? Number(e.target.value) : '')
                setAttrs({})
                setOverride(false)
              }}
            >
              <option value="">Choose…</option>
              {types.map((t) => (
                <option key={t.cert_type_id} value={t.cert_type_id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="form-field">
          <label htmlFor="cfIssued">Issued</label>
          <input id="cfIssued" type="date" value={issued} onChange={(e) => setIssued(e.target.value)} />
        </div>

        {type?.expiry_rule === 'CardDate' && (
          <div className="form-field">
            <label htmlFor="cfExpires">Expires (as printed on the card)</label>
            <input id="cfExpires" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
          </div>
        )}
        {type?.expiry_rule === 'FixedMonths' && (
          <div className="form-field">
            <label htmlFor="cfExpires">Expires</label>
            {override ? (
              <input id="cfExpires" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
            ) : (
              <p style={{ margin: '0.4rem 0' }}>
                {computed ? dayLabel(computed) : '—'}{' '}
                <span className="muted">({type.validity_months} months from issue)</span>
              </p>
            )}
            <label className="checkline" style={{ fontWeight: 400 }}>
              <input
                type="checkbox"
                checked={override}
                onChange={(e) => {
                  setOverride(e.target.checked)
                  if (e.target.checked) setExpires(computed ?? '')
                }}
              />{' '}
              The card shows a different date
            </label>
          </div>
        )}

        <div className="form-field">
          <label htmlFor="cfNumber">Card or certificate number</label>
          <input id="cfNumber" value={number} onChange={(e) => setNumber(e.target.value)} />
        </div>
        <div className="form-field">
          <label htmlFor="cfProvider">Trained / issued by</label>
          <input id="cfProvider" value={provider} onChange={(e) => setProvider(e.target.value)} />
        </div>
        {type?.attribute_spec.map((s) => (
          <div className="form-field" key={s.key}>
            <label htmlFor={`cfAttr-${s.key}`}>
              {s.label}
              {s.required && ' *'}
            </label>
            <input
              id={`cfAttr-${s.key}`}
              value={attrs[s.key] ?? ''}
              onChange={(e) => {
                const v = e.target.value
                setAttrs((a) => {
                  const next = { ...a }
                  if (v.trim()) next[s.key] = v
                  else delete next[s.key]
                  return next
                })
              }}
            />
          </div>
        ))}
        <div className="form-field">
          <label htmlFor="cfNote">Note</label>
          <input id="cfNote" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        {needsReason && (
          <div className="form-field">
            <label htmlFor="cfReason">Why the date is {correcting ? 'changing' : 'different'} *</label>
            <input id="cfReason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
        )}
      </div>

      {type && (
        <p className="muted" style={{ fontSize: '0.8125rem' }}>
          {EXPIRY_RULE_LABEL[type.expiry_rule]}
          {type.is_restricted && ' · Restricted: only Admins see the detail.'}
        </p>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          className="btn btn-sm"
          disabled={busy || !type || !issued || (needsReason && !reason.trim())}
          onClick={() => void submit()}
        >
          {busy ? 'Saving…' : correcting ? 'Save correction' : 'Save'}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  )
}

/**
 * Where this person's expiry alerts go (migration 30). Field staff have no
 * address anywhere else -- Sage holds none -- so this is where one is
 * entered. Blank falls back to their office sign-in address, if any.
 */
function AlertEmailCard({
  employeeId,
  contact,
  onSaved,
  onError,
}: {
  employeeId: number
  contact: CertNotifyContact
  onSaved: (c: CertNotifyContact) => void
  onError: (msg: string | null) => void
}) {
  const [editing, setEditing] = useState(false)
  const [email, setEmail] = useState(contact.alert_email ?? '')
  const [optOut, setOptOut] = useState(contact.opt_out)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    setEmail(contact.alert_email ?? '')
    setOptOut(contact.opt_out)
  }, [contact])

  async function save() {
    onError(null)
    setBusy(true)
    try {
      onSaved(await api.setCertContact(employeeId, { alert_email: email.trim() || null, opt_out: optOut }))
      setEditing(false)
    } catch (err) {
      onError(errText(err, 'Could not save the alert email.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Expiry alerts</h3>
      {!editing ? (
        <div className="crew-heading">
          <p style={{ margin: 0 }}>
            {contact.opt_out ? (
              <span className="muted">Opted out — no emails to this person.</span>
            ) : contact.effective_email ? (
              <>
                Emails go to <b>{contact.effective_email}</b>
                {!contact.alert_email && <span className="muted"> (their sign-in address)</span>}
              </>
            ) : (
              <span className="muted">No email on file — alerts reach the office summary only.</span>
            )}
          </p>
          <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
            Change
          </button>
        </div>
      ) : (
        <div className="field-grid">
          <div className="form-field">
            <label htmlFor="alertEmail">Alert email</label>
            <input
              id="alertEmail"
              type="email"
              value={email}
              placeholder={contact.login_email ?? 'name@example.com'}
              onChange={(e) => setEmail(e.target.value)}
            />
            {contact.login_email && (
              <span className="muted">Leave blank to use {contact.login_email}.</span>
            )}
          </div>
          <label className="checkline" style={{ alignSelf: 'end' }}>
            <input type="checkbox" checked={optOut} onChange={(e) => setOptOut(e.target.checked)} /> Don't email
            this person
          </label>
          <div className="form-field" style={{ alignSelf: 'end', display: 'flex', gap: 8 }}>
            <button className="btn" disabled={busy} onClick={() => void save()}>
              Save
            </button>
            <button className="btn btn-secondary" disabled={busy} onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
